import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import api, { getApiErrorMessage } from '../../services/api';
import ReportCard from '../components/ReportCard';
import ReportRequestForm from '../components/ReportRequestForm';
import '../ChatApp.css';

const REPORT_ANCHOR_DAYS = 30;

function mapHistoryMessage(msg) {
  const role = msg.role === 'user' ? 'user' : 'bot';
  const reportCard = msg.report_card || null;
  return {
    id: msg.id,
    role,
    text: msg.text || '',
    reportDate: msg.report_date || reportCard?.nav?.date_from || reportCard?.pdf?.date_from || null,
    reportCard: reportCard && typeof reportCard === 'object' ? reportCard : null,
    createdAt: msg.created_at || null,
  };
}

function formatMessageDate(dateString, { t, language }) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t('support.justNow');
  if (minutes < 60) return t('support.minutesAgo', { minutes });
  const localeMap = { en: 'en-US', ru: 'ru-RU', zh: 'zh-CN', tr: 'tr-TR', ar: 'ar-SA' };
  const locale = localeMap[language] || 'en-US';
  if (diff < 86400000) {
    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  }
  return `${date.toLocaleDateString(locale)} ${date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`;
}

function isoDaysAgo(days) {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function buildAnchorDates(days = REPORT_ANCHOR_DAYS) {
  const out = [];
  for (let i = 1; i <= days; i += 1) {
    out.push(isoDaysAgo(i));
  }
  return out;
}

function formatAnchorLabel(isoDate, language) {
  const date = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  const localeMap = { en: 'en-US', ru: 'ru-RU', zh: 'zh-CN', tr: 'tr-TR', ar: 'ar-SA' };
  const locale = localeMap[language] || 'en-US';
  return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
}

function messageReportDate(msg) {
  if (!msg) return null;
  if (msg.reportDate) return String(msg.reportDate).slice(0, 10);
  const card = msg.reportCard;
  if (!card) return null;
  return (
    card?.nav?.date_from
    || card?.pdf?.date_from
    || card?.period
    || null
  );
}

export default function AiAssistantTopic({ storeId, stores, onNavigateDeductions, onNavigateReturns }) {
  const { t, language } = useLanguage();
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [anchorLoading, setAnchorLoading] = useState('');
  const messagesEndRef = useRef(null);
  const reportRefs = useRef({});

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const anchorDates = useMemo(() => buildAnchorDates(REPORT_ANCHOR_DAYS), []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.chatUiAiHistory({
        conversation_id: conversationId || undefined,
        store_id: storeId || undefined,
      });
      const data = res?.data ?? res;
      if (data?.conversation_id) setConversationId(data.conversation_id);
      const rows = Array.isArray(data?.messages) ? data.messages.map(mapHistoryMessage) : [];
      if (rows.length) {
        setMessages(rows);
      } else {
        setMessages([{ role: 'bot', text: t('chatApp.aiWelcome') }]);
      }
    } catch {
      setMessages([{ role: 'bot', text: t('chatApp.aiWelcome') }]);
    } finally {
      setHistoryLoaded(true);
    }
  }, [conversationId, storeId, t]);

  useEffect(() => {
    setHistoryLoaded(false);
    loadHistory();
  }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const appendReportMessages = useCallback((data, fallbackUserText) => {
    if (data?.conversation_id) setConversationId(data.conversation_id);
    if (!data?.report_card) {
      throw new Error(t('chatApp.aiError'));
    }
    const reportDate = data.report_card?.nav?.date_from || data.report_card?.pdf?.date_from || null;
    setMessages((m) => [
      ...m,
      {
        role: 'user',
        text: data.user_message || fallbackUserText || t('chatApp.reportGeneral'),
        createdAt: new Date().toISOString(),
      },
      {
        role: 'bot',
        text: '',
        reportDate,
        reportCard: data.report_card,
        id: data.assistant_message_id,
        createdAt: new Date().toISOString(),
      },
    ]);
    return reportDate;
  }, [t]);

  const handleReportRequested = async ({ weekly, from, to, storeId: formStoreId, conversationId: convId }) => {
    const res = await api.chatUiReportsRequest({
      conversation_id: convId || conversationId || undefined,
      kind: weekly ? 'weekly' : 'general',
      date_from: from,
      date_to: to,
      store_id: formStoreId || undefined,
      lang: language,
    });
    const data = res?.data ?? res;
    appendReportMessages(data, t('chatApp.reportGeneral'));
  };

  const scrollToReportDate = (isoDate) => {
    const el = reportRefs.current[isoDate];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('chat-bubble--report-flash');
      window.setTimeout(() => el.classList.remove('chat-bubble--report-flash'), 1600);
      return true;
    }
    return false;
  };

  const openAnchorReport = async (isoDate) => {
    if (anchorLoading) return;
    if (scrollToReportDate(isoDate)) return;

    setAnchorLoading(isoDate);
    try {
      const res = await api.chatUiReportsRequest({
        conversation_id: conversationId || undefined,
        kind: 'general',
        date_from: isoDate,
        date_to: isoDate,
        store_id: storeId || undefined,
        lang: language,
      });
      const data = res?.data ?? res;
      const reportDate = appendReportMessages(
        data,
        t('chatApp.viewReportForDate', { date: isoDate }),
      );
      window.setTimeout(() => {
        scrollToReportDate(reportDate || isoDate);
      }, 80);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'bot',
          text: getApiErrorMessage(err, t('chatApp.aiError')),
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setAnchorLoading('');
    }
  };

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: q, createdAt: new Date().toISOString() }]);
    setLoading(true);
    try {
      const res = await api.chatUiAiAsk({
        question: q,
        store_id: storeId || undefined,
        conversation_id: conversationId || undefined,
        lang: language,
      });
      const data = res?.data ?? res;
      if (data?.conversation_id) setConversationId(data.conversation_id);
      const answer = data?.answer?.trim();
      const reportCard = data?.report_card || null;
      if (answer || reportCard) {
        setMessages((m) => [...m, {
          role: 'bot',
          text: answer || '',
          reportDate: reportCard?.nav?.date_from || reportCard?.pdf?.date_from || null,
          reportCard,
          createdAt: new Date().toISOString(),
        }]);
      } else {
        setMessages((m) => [...m, {
          role: 'bot',
          text: t('chatApp.aiError'),
          createdAt: new Date().toISOString(),
        }]);
      }
    } catch (err) {
      const code = err?.data?.error;
      const fallback =
        code === 'gpt_unavailable'
          ? t('chatApp.aiUnavailable')
          : getApiErrorMessage(err, t('chatApp.aiError'));
      setMessages((m) => [...m, {
        role: 'bot',
        text: fallback,
        createdAt: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (!historyLoaded) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div className="chat-app-ai">
      <div className="chat-report-anchors" aria-label={t('chatApp.viewReportFor')}>
        <div className="chat-report-anchors-label">{t('chatApp.viewReportFor')}</div>
        <div className="chat-report-anchors-list">
          {anchorDates.map((isoDate) => (
            <button
              key={isoDate}
              type="button"
              className={`chat-report-anchor-btn${anchorLoading === isoDate ? ' is-loading' : ''}`}
              onClick={() => openAnchorReport(isoDate)}
              disabled={Boolean(anchorLoading)}
              title={isoDate}
            >
              {anchorLoading === isoDate ? (
                <Spinner size="sm" animation="border" />
              ) : (
                formatAnchorLabel(isoDate, language)
              )}
            </button>
          ))}
        </div>
      </div>
      <ReportRequestForm
        stores={stores}
        storeExternalId={storeId}
        conversationId={conversationId}
        onReportRequested={handleReportRequested}
      />
      <div className="chat-app-ai-messages">
        {messages.map((msg, i) => {
          const reportIso = messageReportDate(msg);
          const bubbleKey = msg.id || `msg-${i}`;
          return (
            <div key={bubbleKey} className={`chat-bubble-row ${msg.role}`}>
              <div
                className={`chat-bubble ${msg.reportCard ? 'chat-bubble--report' : ''}`}
                ref={(el) => {
                  if (msg.reportCard && reportIso && el) {
                    reportRefs.current[reportIso] = el;
                  }
                }}
                data-report-date={reportIso || undefined}
              >
                {msg.reportCard ? (
                  <ReportCard
                    card={msg.reportCard}
                    onNavigateDeductions={onNavigateDeductions}
                    onNavigateReturns={onNavigateReturns}
                  />
                ) : (
                  msg.text
                )}
                {msg.createdAt && (
                  <div className="chat-bubble-time">
                    {formatMessageDate(msg.createdAt, { t, language })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {loading && (
          <div className="chat-bubble-row bot">
            <div className="chat-bubble"><Spinner size="sm" /> …</div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="chat-ai-input-bar">
        <input
          className="form-control"
          placeholder={t('chatApp.aiPlaceholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
        />
        <Button className="btn-gradient" onClick={send} disabled={loading || !input.trim()}>
          {t('chatApp.send')}
        </Button>
      </div>
    </div>
  );
}
